import React, { CSSProperties, ReactElement, ReactNode } from 'react';
import styled from 'styled-components';
import { TDirection } from './HBDialog.types';
import HBModal, { IModal, IAction } from '../HBModal/HBModal';
import { ActionSpacer, Body } from '../HBModal/HBModal.styles';
import HBButton, { IButtonProps } from '../HBButton/HBButton';
import spacing from '../../styles/spacing';
import HBText from '../HBText/HBText';
import HBBanner, { IBannerTheme } from '../HBBanner/HBBanner';
import { vars } from '../../theme';
import {
    TriangleExclamationMark42,
    InfoCircle42,
    CheckCircle42
} from '@honeybook/icons';
import { AnimationProps, motion } from 'framer-motion';

export type TDialogType = 'error' | 'warning' | 'info' | 'success';

const DIALOG_TYPE_PROPS: Record<TDialogType, { icon: ReactElement }> = {
    error: {
        icon: <TriangleExclamationMark42 color={vars.colors.text.primary} />
    },
    info: {
        icon: <InfoCircle42 color={vars.colors.text.primary} />
    },
    success: {
        icon: <CheckCircle42 color={vars.colors.text.primary} />
    },
    warning: {
        icon: <TriangleExclamationMark42 color={vars.colors.text.primary} />
    }
} as const;

export interface IDialogInlineBanner {
    text: ReactNode;
    /** Custom color theme applied to the HBBanner; controls background and text colors. */
    bannerCustomTheme?: IBannerTheme;
    /** Renders the banner above the icon ('top', default) or below the description ('bottom'). */
    position?: 'top' | 'bottom';
    /** CSS margin shorthand applied to the banner container. Defaults to `'0 0 14px'` for top position. */
    margin?: string;
}

export interface IDialog extends Omit<IModal, 'children'> {
    /** Selects the icon displayed in the dialog. 'error' and 'warning' use a triangle exclamation mark; 'info' uses a circle info icon; 'success' uses a check circle. */
    type?: TDialogType;
    /** Custom icon element rendered above the title, overriding the icon selected by `type`. */
    icon?: ReactElement;
    /** Body text rendered below the title. Preserves newlines via `white-space: pre-line`. */
    description?: ReactNode;
    /** Layout direction for the footer action buttons. 'horizontal' renders them in a row; 'vertical' stacks them full-width. Defaults to 'horizontal'. */
    actionsDirection?: TDirection;
    /** Inline styles applied to the modal body container. */
    bodyStyles?: CSSProperties;
    /** When true, prevents the user from dismissing the dialog by clicking the backdrop or pressing Escape. */
    isRequiredPrompt?: boolean;
    /** Called when the dialog requests to be closed (close button, backdrop click, or Escape key). */
    onClose?(): void;
    /** Renders a themed HBBanner inside the dialog. Position defaults to 'top' (above the icon); use 'bottom' to place it below the description. */
    inlineBanner?: IDialogInlineBanner;
    /** Label for the underlined link rendered below the description. Requires `linkAction`. */
    linkText?: string;
    /** Callback invoked when the user clicks the link rendered by `linkText`. */
    linkAction?: () => void;
    /** Inline styles applied to the inner content container element. */
    containerStyles?: CSSProperties;
    /** CSS margin applied to the title. Defaults to `'16px 0 0 0'`; overridden to `'0'` when `theme='ai'`. */
    titleMargin?: string;
    /** CSS class applied to the root element. */
    className?: string;
    /** Sets the `data-testid` attribute on the inner content container for test queries. */
    testId?: string;
    /** Visual theme variant. 'ai' applies the AI gradient header and hides the type icon. */
    theme?: 'ai';
}

const Container = styled(motion.div)`
    text-align: center;
`;

const Title = styled(HBText)<{ titleMargin: string }>`
    margin: ${props => props.titleMargin};
`;

const ActionLink = styled(HBText)`
    margin-top: 10px;
    display: flex;
    justify-content: center;
    cursor: pointer;
    text-decoration: underline;
`;

const Description = styled(HBText)`
    margin-top: 10px;
    white-space: pre-line;
`;

const StyledActionButton = styled(HBButton)`
    width: auto;
`;

const BannerContent = styled.div`
    font-size: 12px;
    justify-content: flex-start;
`;

export const Actions = styled.div<{ actionsDirection: TDirection }>`
    display: inline-flex;
    justify-content: center;
    flex-direction: ${props =>
        props.actionsDirection === 'vertical' ? 'column' : 'row'};
    margin-top: ${spacing[24]};

    ${StyledActionButton} {
        width: ${props =>
            props.actionsDirection === 'vertical' ? '100%' : 'auto'};
    }
`;

export const VerticalActionSpacer = styled.div`
    margin-top: 12px;
`;

const StyledHBModal = styled(HBModal)`
    ${Body} {
        padding: 24px;
    }
`;

function DynamicActionSpacer({ actionsDirection, children }) {
    return actionsDirection === 'vertical' ? (
        <VerticalActionSpacer>{children}</VerticalActionSpacer>
    ) : (
        <ActionSpacer>{children}</ActionSpacer>
    );
}

function ActionButton({
    action,
    actionsDirection
}: {
    action: IAction;
    actionsDirection: TDirection;
}) {
    const {
        customButton,
        size = 'sm',
        scope = 'system',
        ...actionProps
    } = action;
    const ButtonComponent = (customButton ||
        StyledActionButton) as React.ComponentType<IButtonProps>;
    const buttonTheme = customButton ? 'custom' : action.theme;

    return (
        <DynamicActionSpacer actionsDirection={actionsDirection}>
            <ButtonComponent
                size={size}
                scope={scope}
                buttonTheme={buttonTheme}
                testId={
                    action.testIdName
                        ? `hb-dialog-${action.testIdName}--button`
                        : undefined
                }
                testIdType="hb-dialog-action--button"
                {...actionProps}
            >
                {action.text}
            </ButtonComponent>
        </DynamicActionSpacer>
    );
}

const HBDialogInlineBanner = ({
    bannerCustomTheme,
    text,
    margin
}: IDialogInlineBanner) => {
    return (
        <HBBanner
            testId="dialog-inline--banner"
            customTheme={bannerCustomTheme}
            margin={margin}
        >
            <BannerContent>{text}</BannerContent>
        </HBBanner>
    );
};

function getIcon(type?: TDialogType, icon?: ReactElement) {
    return icon || DIALOG_TYPE_PROPS[type]?.icon;
}

const DEFAULT_TITLE_MARGIN = `${spacing[16]} 0px 0px 0px`;

const HBDialog = ({
    actions,
    title,
    description,
    icon,
    maxWidth = '380px',
    titleMargin = DEFAULT_TITLE_MARGIN,
    actionsDirection = 'horizontal',
    linkText,
    linkAction,
    inlineBanner,
    testId,
    type,
    theme,
    ...modalProps
}: IDialog) => {
    const isAiTheme = theme === 'ai';
    return (
        <StyledHBModal
            maxWidth={maxWidth}
            {...(isAiTheme ? { theme: 'ai' } : {})}
            {...modalProps}
        >
            <HBDialogContent
                testId={testId}
                inlineBanner={inlineBanner}
                type={type}
                icon={icon}
                titleMargin={titleMargin}
                title={title}
                description={description}
                linkText={linkText}
                linkAction={linkAction}
                actions={actions}
                actionsDirection={actionsDirection}
                isAiTheme={isAiTheme}
            />
        </StyledHBModal>
    );
};

export default HBDialog;

export function HBDialogContent({
    testId,
    inlineBanner,
    type,
    icon,
    titleMargin,
    title,
    description,
    linkText,
    linkAction,
    actions,
    actionsDirection,
    animation,
    isAiTheme
}: Pick<
    IDialog,
    | 'testId'
    | 'inlineBanner'
    | 'type'
    | 'icon'
    | 'titleMargin'
    | 'title'
    | 'description'
    | 'linkText'
    | 'linkAction'
    | 'actions'
    | 'actionsDirection'
> & { animation?: AnimationProps; isAiTheme?: boolean }) {
    const resolvedIcon = !isAiTheme ? getIcon(type, icon) : undefined;
    return (
        <Container data-testid={testId} {...animation}>
            {inlineBanner && inlineBanner?.position !== 'bottom' && (
                <HBDialogInlineBanner
                    bannerCustomTheme={inlineBanner.bannerCustomTheme}
                    text={inlineBanner.text}
                    margin="0 0 14px"
                />
            )}
            {resolvedIcon && <span data-part="icon">{resolvedIcon}</span>}
            <Title
                tag="div"
                type="title18"
                titleMargin={isAiTheme ? '0' : titleMargin}
                testId="hb-dialog-title"
            >
                {title}
            </Title>
            {description && (
                <Description
                    tag="div"
                    type="body14"
                    testId="hb-dialog-description"
                >
                    {description}
                </Description>
            )}
            {inlineBanner && inlineBanner?.position === 'bottom' && (
                <HBDialogInlineBanner
                    bannerCustomTheme={inlineBanner.bannerCustomTheme}
                    text={inlineBanner.text}
                />
            )}
            {linkText && linkAction && (
                <ActionLink
                    type="body14"
                    onClick={linkAction}
                    testId="hb-dialog-link"
                >
                    {linkText}
                </ActionLink>
            )}

            {actions && (
                <Actions actionsDirection={actionsDirection}>
                    {actions.map((action, index) => (
                        <ActionButton
                            key={index}
                            action={action}
                            actionsDirection={actionsDirection}
                        />
                    ))}
                </Actions>
            )}
        </Container>
    );
}
